import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, Dialog, Icon } from '@node-space/ui-components'
import { useFiltersModal } from 'hooks/useFiltersModal'
import { customerTypeLabelMap, statusLabelMap } from '../utils/utils'

export interface CustomersV2Filters {
  [key: string]: string[] | undefined
  statuses?: string[]
  types?: string[]
}

interface CustomersV2FiltersModalProps {
  appliedFilters: CustomersV2Filters
  onApply: (filters: CustomersV2Filters) => void
  onResetAll: () => void
}

export const CustomersV2FiltersModal = ({
  appliedFilters,
  onApply,
  onResetAll,
}: CustomersV2FiltersModalProps) => {
  const { t } = useTranslation()
  const {
    isOpen,
    setIsOpen,
    localStatuses,
    localTypes,
    toggleStatus,
    toggleType,
    handleOpen,
    handleApply,
    handleReset,
  } = useFiltersModal({ appliedFilters, onApply, onResetAll })

  const appliedCount =
    (appliedFilters?.statuses?.length ?? 0) + (appliedFilters?.types?.length ?? 0)

  return (
    <>
      <Button
        variant="secondary"
        iconElement={<Icon name="Funnel" />}
        onClick={handleOpen}
        testId="customer-v2-filters-btn"
      >
        {appliedCount ? `${t('filters')} (${appliedCount})` : t('filters')}
      </Button>

      <Dialog
        title={t('customers.v2FiltersTitle')}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        type="utility"
        ActionButtons={[
          <Button key="show" onClick={handleApply} testId="v2-filters-apply-btn">
            {t('show')}
          </Button>,
          <Button
            key="reset"
            variant="secondary"
            onClick={handleReset}
            testId="customer-v2-filters-reset-btn"
          >
            {t('resetAll')}
          </Button>,
        ]}
      >
        <Box flexCol gapY={16}>
          <Combobox
            id="customer-v2-filter-statuses"
            label={t('status')}
            selectPlaceholder={t('status')}
            filter={false}
            modal
            checkedValues={localStatuses}
            onCheckedChange={toggleStatus}
            items={Object.keys(statusLabelMap).map(status => ({
              value: status,
              leftText: statusLabelMap[status as keyof typeof statusLabelMap],
            }))}
            testId="customer-v2-filter-statuses"
          />
          <Combobox
            id="customer-v2-filter-types"
            label={t('customers.type')}
            selectPlaceholder={t('customers.type')}
            filter={false}
            modal
            checkedValues={localTypes}
            onCheckedChange={toggleType}
            items={Object.entries(customerTypeLabelMap).map(([value, label]) => ({
              value,
              leftText: label,
            }))}
            testId="customer-v2-filter-types"
          />
        </Box>
      </Dialog>
    </>
  )
}
